import { useState, ReactNode, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { useLanguage } from '@/hooks/useLanguage';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';

interface DashboardLayoutProps {
  children: ReactNode;
  breadcrumb?: string[];
}

export default function DashboardLayout({ children, breadcrumb }: DashboardLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { language, setLanguage } = useLanguage();
  const { isDark } = useTheme();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (mobileOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen, isMobile]);

  const sidebarWidth = collapsed ? 60 : 220;

  // Auth gate: no logged-in user (mock login form AND Telegram SSO both populate
  // localStorage 'helios_auth_user') → bounce to /login. Protects the whole
  // cabinet from direct-URL access (was open to "Гість").
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <div
      className="min-h-screen"
      style={{
        background: isDark ? '#111113' : '#F5F5F7',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Mobile backdrop */}
      {mobileOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        isMobile={isMobile}
        language={language}
      />

      <Topbar
        sidebarWidth={sidebarWidth}
        breadcrumb={breadcrumb}
        language={language}
        onLanguageChange={setLanguage}
        onMobileMenuToggle={() => setMobileOpen(!mobileOpen)}
        isMobile={isMobile}
      />

      <main
        className="pt-[72px] min-h-screen transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : `${sidebarWidth}px` }}
      >
        <div className="p-4 md:p-6 text-gray-900 dark:text-gray-100">
          {children}
        </div>
      </main>
    </div>
  );
}